import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Card, Form, Input } from 'antd';
import { MainContext } from '../../../layouts/Main/Context';
import ApiService from '../../../services/ApiService';
import DataService from '../../../services/DataService';
import Utils from '../../../utils';

const StyledCard = styled(Card)`
  .ant-card-body {
    max-height: calc(100vh - 190px);
    overflow-y: auto;
  }
`;

export const SimulationCountryDetails = () => {
  const navigate = useNavigate();
  const { setBreadcrumbItems } = useContext(MainContext);
  const { simulationId, simulationCountryId } = useParams();
  const [simulationCountry, setSimulationCountry] = useState(null);

  const resourceTypeList = DataService.getItem('resourceTypeList');
  const resourceList = DataService.getItem('resourceList');

  const fetchSimulation = async () => {
    try {
      const { result: simulationData } = await ApiService.retrieveSimulation(simulationId);
      const { resultList: countryList } = await ApiService.retrieveSimulationCountryList(simulationId);
      const countryData = countryList.find(c => c.simulationCountryId === +simulationCountryId);
      setSimulationCountry(countryData);
      setBreadcrumbItems([
        {
          title: 'Simulations',
          path: '/main/simulations',
        },
        {
          title: simulationData.simulationName,
          path: `/main/simulations/${simulationId}`,
        },
        {
          title: 'Countries',
          path: `/main/simulations/${simulationId}/countries`,
        },
        {
          title: countryData.countryName,
        },
      ]);
    } catch (e) {
      //
    }
  };

  const getResourceValue = resourceName => {
    const resource = resourceList.find(r => r.resourceNameLocalized === resourceName);
    const resourceObject = simulationCountry?.simulationCountryResourceViewList.find(
      r => r.resourceId === resource.resourceId,
    );
    return resourceObject?.remainingQuantity ?? 0;
  };

  const getResourceTypeValue = resourceTypeName => {
    const resourceType = resourceTypeList.find(rt => rt.resourceTypeNameLocalized === resourceTypeName);
    const resources = resourceList.filter(r => r.resourceTypeId === resourceType.resourceTypeId);
    const value = resources.reduce((total, resource) => total + getResourceValue(resource.resourceNameLocalized), 0);
    return value;
  };

  const handleManageStudents = () => {
    navigate(`/main/simulations/${simulationId}/countries/${simulationCountryId}/students`);
  };

  const handleManageResources = () => {
    navigate(`/main/simulations/${simulationId}/countries/${simulationCountryId}/resources`);
  };

  const handleManageGoals = () => {
    navigate(`/main/simulations/${simulationId}/countries/${simulationCountryId}/goals`);
  };

  const ActionBar = (
    <>
      <Button size="small" style={{ marginRight: 8 }} type="primary" onClick={handleManageStudents}>
        Students
      </Button>
      <Button size="small" style={{ marginRight: 8 }} type="primary" onClick={handleManageResources}>
        Resources
      </Button>
      <Button size="small" type="primary" onClick={handleManageGoals}>
        Goals
      </Button>
    </>
  );

  useEffect(() => {
    fetchSimulation();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <StyledCard size="small" title="Manage Simulation Country" extra={ActionBar}>
        <Form labelCol={{ span: 4 }}>
          <Form.Item label="Country">
            <Input readOnly value={simulationCountry?.countryName} />
          </Form.Item>
          <Form.Item label="Country Name">
            <Input readOnly value={simulationCountry?.countryName} />
          </Form.Item>
          <Form.Item label="Current Year">
            <Input readOnly value={simulationCountry?.simulationCountryYear} />
          </Form.Item>
          <Form.Item label="Current State">
            <Input readOnly value={Utils.getSimulationCountryStateById(simulationCountry?.simulationCountryState)} />
          </Form.Item>
          <Form.Item label="Goals Set">
            <Input readOnly value={simulationCountry?.goalsAreLocked ? 'Goals Locked' : 'Goals Unlocked'} />
          </Form.Item>
          <Form.Item label="National Wealth">
            <Input readOnly value={simulationCountry?.gdp} />
          </Form.Item>
          <Form.Item label="Gross Domestic Product (GDP)">
            <Input readOnly value={simulationCountry?.gdp} />
          </Form.Item>
          <Form.Item label="Population">
            <Input readOnly value={simulationCountry?.population} />
          </Form.Item>
          <Form.Item label="GDP Per Capita">
            <Input readOnly value={simulationCountry?.gpc?.toFixed(2)} />
          </Form.Item>
          <Form.Item label="Water Resources">
            <Input readOnly value={getResourceTypeValue('Water Resources')} />
          </Form.Item>
          <Form.Item label="Land and Mineral Resources">
            <Input readOnly value={getResourceTypeValue('Agricultural Land')} />
          </Form.Item>
          <Form.Item label="Pollution">
            <Input readOnly value={simulationCountry?.pollution} />
          </Form.Item>
          <Form.Item label="Fertility">
            <Input readOnly value={`${Math.round((simulationCountry?.fertility ?? 0) * 100)}%`} />
          </Form.Item>
          <Form.Item label="Migrated Population">
            <Input readOnly value={simulationCountry?.migratedPopulation} />
          </Form.Item>
          <Form.Item label="Deaths">
            <Input readOnly value={simulationCountry?.deceasedPopulation} />
          </Form.Item>
          <Form.Item label="Military">
            <Input readOnly value={getResourceTypeValue('Military')} />
          </Form.Item>
          <Form.Item label="Gold">
            <Input readOnly value={getResourceValue('Gold')} />
          </Form.Item>
          <Form.Item label="Score">
            <Input readOnly value={simulationCountry?.simulationCountryScore?.toFixed(2)} />
          </Form.Item>
        </Form>
      </StyledCard>
    </>
  );
};
